import Grid from '@material-ui/core/Grid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import React, { useEffect, useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet';

import useAccountDetailList from '../../../hooks/useAccountDetailList';
import { useTranslation } from '../../../hooks/useTranslation';
import colors from '../../../themes/main-colors';
import AccountLayout from '../../account-layout/AccountLayout';
import Alerts from '../../alerts';
import SecureSitePromo from '../../secure-site-promo';
import AccountList from './AccountList';
import { navigate } from '@reach/router';
import ROUTES from '../../../routes';
import SortFilterPageHeader from '../sort-filter-page-header/SortFilterPageHeader';
import { format, parseISO } from 'date-fns';
import AccountGroupDropdown from '../../account-group-dropdown';
import { getAccountsList } from './queries';
import Backdrop from '../../backdrop';
import useSelectedGroupId from '../../../hooks/useSelectedGroupId';
import useSelectedAccountParams from '../../../hooks/useSelectedAccountParams';
import useAccountMulticheckState from '../../../hooks/useAccountMulticheckState';
import { AccountDetail, AccountParams } from '../../../__generated__/pge-types';
import BroadcastMessage from '../broadcast-message/BroadcastMessage';
import useAccountListHeader from './useAccountListHeader';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';
import { NickNameAddressPayload } from '../../nickname-change-modal/types';
import useAccountsStore from '../../../hooks/useAccountsStore';
import { PublicSafetyPowerShutoffDetails } from '../../public-safety-power-shutoff/PublicSafetyPowerShutoffDetails';
import PaySelectedAccountsCard from './PaySelectedAccountsCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainArea: {
      minWidth: '50em',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        padding: 0,
        maxWidth: '75em',
      },
      [theme.breakpoints.down('md')]: {
        minWidth: '15em',
      },
    },
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    buttonRow: {
      marginTop: '2em',
      display: 'flex',
      justifyContent: 'flex-end',
      flexDirection: 'row',
      alignItems: 'center',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        justifyContent: 'center',
      },
    },
    buttonContainer: {
      display: 'flex',
      marginLeft: '4px',
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        marginTop: '1em',
        width: '100%',
        justifyContent: 'center',
      },
    },
    button: {
      background: colors.orange,
      [theme.breakpoints.down('md')]: {
        width: '80%',
      },
    },
    paySelectedCard: {
      marginTop: 12,
      position: 'relative',
      // Large desktop screens
      '@media (min-width:1536px)': {
        position: 'absolute',
        top: 0,
        right: -320, // ✅ better to use number instead of string
      },
    },
  }),
);

type Props = {
  onSelectAccount?: (account: AccountDetail) => void;
};

export default function MultiAccountSummary({ onSelectAccount }: Props) {
  const {
    accountListParams,
    onResetParams,
    onAccountSearch,
    onAccountSort,
    onAccountPageChange,
  } = useAccountListHeader();

  const { t, inlineRichT } = useTranslation();
  const { selectedGroupId } = useSelectedGroupId();
  const { setAccountDisconnectedStatus } = useAccountsStore();
  const { setSelectedAccountParams } = useSelectedAccountParams();
  const { isClosedAccountsEnabled } = useContext(featureFlagsContext);
  const { isOutagesPSPSEnabled } = useContext(featureFlagsContext);

  const {
    loading,
    error,
    detailList,
    accounts,
    totalCount = 0,
    writeAccountInfoCache,
  } = useAccountDetailList(getAccountsList, selectedGroupId, {
    accountListParams,
  });

  const {
    customerAccountState: { hasInactiveAccounts },
  } = useAccountsStore();
  const accountsListCacheUpdate = (payload: NickNameAddressPayload) => {
    const idx = accounts.findIndex(a =>
      a.premiseInfo?.find(
        p => p?.encryptedPremiseId === payload.encryptedPremiseId,
      ),
    );
    accounts[idx]?.premiseInfo?.some(premise => {
      if (premise?.encryptedPremiseId === payload.encryptedPremiseId) {
        return premise?.saDetails?.some(serviceAddress => {
          if (serviceAddress?.encryptedSAId === payload.encryptedSaId) {
            serviceAddress.nickName = payload.nickName;
            return true;
          }
          return false;
        });
      }
      return false;
    });
    writeAccountInfoCache({ accounts: accounts });
  };

  const multicheckState = useAccountMulticheckState({
    query: accountListParams.query,
  });

  useEffect(() => {
    setAccountDisconnectedStatus(false, false, false);
  }, []);

  useEffect(() => {
    if (multicheckState.isCurrentChargesError) {
      typeof window !== 'undefined' && window.scrollTo(0, 0);
    }
  }, [multicheckState.isCurrentChargesError]);

  const getAccountParams = () => {
    return accounts.map(account => {
      return {
        accountNumber: account.accountNumber,
        encryptedPersonId: account.encryptedPersonId,
      } as AccountParams;
    });
  };

  const classes = useStyles();
  const checkedAccountsKey = multicheckState.checkedOptions().join(',');

  // Detect changes in account selection and recalculate the total amount due
  // Use primitive `checkedAccountsKey` instead of `getSelectedAccounts()`
  // to avoid unnecessary recalculations on every render, since
  // `getSelectedAccounts()` returns a new reference each time
  const totalPaymentAmount = useMemo(() => {
    return multicheckState
      .getSelectedAccounts()
      .reduce(
        (sum: number, account: AccountDetail) =>
          sum +
          (account?.currentCharges && account?.currentCharges?.amountDue > 0
            ? account?.currentCharges?.amountDue || 0
            : 0),
        0,
      );
  }, [checkedAccountsKey]);

  return (
    <>
      <AccountLayout>
        <Helmet>
          <title>{t('MY_ACCOUNTS')}</title>
        </Helmet>

        <div className={classes.mainArea} data-testid="multi-account-list">
          {Boolean(error || multicheckState.isCurrentChargesError) && (
            <Alerts
              isOpen
              severity={'error'}
              variant={'standard'}
              message={t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY')}
            />
          )}
          {(loading || multicheckState.areCurrentChargesLoading) && (
            <Backdrop forceOpen />
          )}

          {Boolean(detailList) && (
            <Grid
              container
              style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
            >
              <Grid item style={{ marginBottom: '1em' }} xs={12}>
                <Typography
                  className={classes.colorTextPrimary}
                  component={'span'}
                  variant={'h1'}
                >
                  {t('MY_ACCOUNTS')}
                </Typography>

                <Grid item md={12}>
                  <Box marginTop={2} marginBottom={0}>
                    {!loading && (
                      <AccountGroupDropdown
                        onSelect={() => {
                          onResetParams();
                        }}
                      />
                    )}
                  </Box>
                  {!loading && (
                    <SortFilterPageHeader
                      onAccountSearch={onAccountSearch}
                      onAccountSort={onAccountSort}
                      onAccountPageChange={onAccountPageChange}
                      accountListParams={accountListParams}
                      totalCount={totalCount}
                    />
                  )}
                </Grid>
              </Grid>

              {!loading && isOutagesPSPSEnabled && (
                <PublicSafetyPowerShutoffDetails
                  accountParams={getAccountParams()}
                  isAccountSelected={false}
                />
              )}

              {!loading && (
                <>
                  <BroadcastMessage />
                  <Grid item xs={12} style={{ position: 'relative' }}>
                    <Grid item className={classes.paySelectedCard}>
                      <PaySelectedAccountsCard
                        totalPaymentAmount={totalPaymentAmount}
                        accountsSelected={
                          multicheckState.checkedOptions().length
                        }
                        areAllWithBalanceSelected={
                          multicheckState.areAllWithBalanceSelected
                        }
                        onSelectAllChange={async checked => {
                          await multicheckState.toggleSelectAllWithBalance(
                            checked,
                          );
                        }}
                        onPaySelected={() =>
                          navigate(ROUTES.MULTI_PAY_PAYMENT_AMOUNTS, {
                            state: {
                              accountParams: multicheckState.getSelectedAccounts(),
                            },
                          })
                        }
                      />
                    </Grid>
                    <AccountList
                      groupId={selectedGroupId}
                      accounts={accounts || []}
                      multicheckState={multicheckState}
                      onViewDetails={account => {
                        if (onSelectAccount) {
                          onSelectAccount(account);
                        }
                        setSelectedAccountParams(selectedGroupId!, account);
                      }}
                      accountsListCacheUpdate={accountsListCacheUpdate}
                    />
                  </Grid>

                  <Grid item xs={12} className={classes.buttonRow}>
                    <Grid item>
                      <Typography variant={'body2'}>
                        {detailList?.timestamp &&
                          `${t('AMOUNT_DUE_INCLUDES')} ${format(
                            parseISO(detailList?.timestamp),
                            'MMM dd, yyyy',
                          )}.`}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} style={{ marginTop: '2em' }}>
                    <SecureSitePromo />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </div>
      </AccountLayout>
    </>
  );
}
